import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd, requestIdleCallback } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    requestIdleCallback(() => {
      this.#cacheTargetAddToCartButton();
      this.#syncStickyThumbnailFromGallery();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  #cacheTargetAddToCartButton() {
    const productForm = this.#getProductForm();
    if (!productForm) return;
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton || this.refs.addToCartButton.disabled) return;

    this.refs.addToCartButton.setAttribute('aria-busy', 'true');
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    const newStickyBar = newStickyAddToCart?.querySelector('[ref="stickyBar"]');

    if (newStickyBar) {
      const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
      const wasVisible = this.refs.stickyBar.classList.contains('is-visible');
      const wasFooterNear = this.refs.stickyBar.classList.contains('is-footer-near');
      const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

      morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

      this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
      this.refs.stickyBar.classList.toggle('is-visible', wasVisible && currentStuck === 'true');
      this.refs.stickyBar.classList.toggle('is-footer-near', wasFooterNear);
      this.dataset.variantAvailable = variantAvailable;
      this.#syncStickyThumbnailFromGallery();
    } else {
      this.#syncFromPdp(variant);
    }

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    if (variant) {
      this.dataset.variantAvailable = variant.available ? 'true' : 'false';
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#cacheTargetAddToCartButton();
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
    this.refs.addToCartButton?.removeAttribute('aria-busy');
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Sync sticky thumbnail from the visible PDP gallery slide when possible.
   */
  #syncStickyThumbnailFromGallery() {
    const stickyImage = this.refs.productImage;
    if (!(stickyImage instanceof HTMLImageElement)) return;

    const section = this.closest('.shopify-section');
    if (!section) return;

    const activeSlide = section.querySelector(
      'slideshow-slide[data-gullyo-media-item]:not([hidden]) img.product-media__image, slideshow-slide[data-gullyo-media-item][aria-hidden="false"] img.product-media__image'
    );

    const nextImageSrc = activeSlide?.currentSrc || activeSlide?.src || '';
    if (!nextImageSrc) return;

    stickyImage.src = nextImageSrc;
    stickyImage.alt = activeSlide?.alt || stickyImage.alt;
  }

  /**
   * Sync sticky content from the live PDP when section HTML is unavailable.
   * @param {ProductVariant | null | undefined} variant
   */
  #syncFromPdp(variant) {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const price = section.querySelector('.product-details .price');
    const stickyPrice = this.querySelector('.sticky-add-to-cart__price');
    if (price && stickyPrice) {
      stickyPrice.innerHTML = price.innerHTML;
    }

    const variantLabel = section.querySelector('.gullyo-pdp__variant-label, .variant-option__swatch-value');
    const stickyVariant = this.querySelector('.sticky-add-to-cart__variant');
    if (stickyVariant && variantLabel?.textContent?.trim()) {
      stickyVariant.textContent = variantLabel.textContent.trim();
      stickyVariant.style.display = '';
    }

    this.#syncStickyThumbnailFromGallery();

    const stickyImage = this.refs.productImage;
    if (stickyImage instanceof HTMLImageElement && !stickyImage.getAttribute('src')) {
      const fallbackSrc = variant?.featured_media?.preview_image?.src || '';
      if (fallbackSrc) {
        stickyImage.src = fallbackSrc;
      }
    }

    const { addToCartButton } = this.refs;
    if (addToCartButton) {
      const available = Boolean(variant?.available);
      addToCartButton.disabled = !available;
      const label = addToCartButton.querySelector('.add-to-cart-text__content span span');
      const mainLabel = this.#targetAddToCartButton?.querySelector('.add-to-cart-text__content span span');
      if (label && mainLabel) {
        label.textContent = mainLabel.textContent.trim();
      }
    }
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
